import React, { useMemo, useCallback } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { Box, Select, MenuItem } from '@mui/material';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

// Hoist currency formatter to avoid recreation on every render
const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/**
 * Editable grid for Pro Forma accounts with Fixed/Variable selection and manual overrides
 */
export default function ProFormaGrid({ 
  accounts, 
  months, 
  manualOverrides, 
  onFixedVariableChange, 
  onManualOverride 
}) {
  
  // Custom cell renderer for Fixed/Variable dropdown
  const FixedVariableRenderer = useCallback((params) => {
    const value = params.value || '';
    
    const handleChange = (event) => {
      const newValue = event.target.value;
      if (onFixedVariableChange) {
        onFixedVariableChange(params.data.glKey, newValue);
      }
    };

    return (
      <Select
        value={value}
        onChange={handleChange}
        size="small"
        fullWidth
        displayEmpty
      >
        <MenuItem value="">Select...</MenuItem>
        <MenuItem value="F">Fixed</MenuItem>
        <MenuItem value="V">Variable</MenuItem>
      </Select>
    );
  }, [onFixedVariableChange]);

  // Column definitions
  const columnDefs = useMemo(() => {
    const cols = [
      {
        headerName: 'Account Name',
        field: 'account_name',
        pinned: 'left',
        width: 200,
        cellStyle: { fontWeight: 'bold' },
      },
      {
        headerName: 'Fixed/Variable',
        field: 'fixedOrVariable',
        width: 150,
        cellRenderer: FixedVariableRenderer,
        editable: false,
      },
      {
        headerName: 'Base Period Total',
        field: 'basePeriodTotal',
        width: 150,
        valueFormatter: params => {
          if (params.value === null || params.value === undefined) return '';
          return currencyFormatter.format(params.value);
        },
      },
    ];

    // Add columns for each month
    months.forEach(month => {
      cols.push({
        headerName: month,
        field: `month_${month}`,
        width: 120,
        editable: true,
        cellEditor: 'agNumberCellEditor', // Use AG Grid's built-in editor
        cellEditorParams: {
          min: 0,
          precision: 2,
        },
        valueFormatter: params => {
          if (params.value === null || params.value === undefined) return '';
          return currencyFormatter.format(params.value);
        },
        cellClassRules: {
          'manual-override': params => {
            // Read manualOverrides from context to avoid stale closure
            const glKey = params.data?.glKey;
            const overrides = params.context?.manualOverrides || {};
            return overrides[glKey]?.[month] !== undefined;
          },
        },
        onCellValueChanged: params => {
          // Handle manual override
          const glKey = params.data.glKey;
          const newValue = params.newValue;
          
          if (onManualOverride) {
            onManualOverride(glKey, month, newValue);
          }
        },
      });
    });

    return cols;
  }, [months, FixedVariableRenderer, onManualOverride]);

  // Row data
  const rowData = useMemo(() => {
    return accounts.map((account, index) => {
      const row = {
        glKey: account.glKey,
        account_name: account.account_name,
        fixedOrVariable: account.fixedOrVariable || '',
        basePeriodTotal: account.basePeriodTotal,
      };

      // Add monthly values (calculated or manually overridden)
      months.forEach(month => {
        const hasOverride = manualOverrides[account.glKey]?.[month] !== undefined;
        const value = hasOverride
          ? manualOverrides[account.glKey][month]
          : account.calculatedMonthly?.[month] || 0;
        
        row[`month_${month}`] = value;
      });

      return row;
    });
  }, [accounts, months, manualOverrides]);

  return (
    <Box className="ag-theme-alpine" sx={{ 
      height: 500, 
      width: '100%',
      '& .manual-override': {
        backgroundColor: '#fff3cd !important', // Light yellow for manual overrides
        fontWeight: 'bold',
      }
    }}>
      <AgGridReact
        columnDefs={columnDefs}
        rowData={rowData}
        domLayout="normal"
        suppressMovableColumns={true}
        context={{ manualOverrides }}
        getRowId={(params) => params.data.glKey}
        defaultColDef={{
          sortable: true,
          resizable: true,
        }}
        tooltipShowDelay={500}
      />
    </Box>
  );
}
